import { Component, ElementRef, EventEmitter, inject, Input, Output } from '@angular/core';
import {
  IContact,
  IOrganization,
  ITag,
} from '../../../../../../types/contactModule';
import { IEntitySegmentQuery } from '../../../../../../components/organization/segments/segment-utilities';
import { take } from 'lodash';
import { LocalCsvService } from '../../../../../../services/local-csv/local-csv.service';
import { formatCount } from '../../../../../../../utils/sms';

/**
 * Everything added to the message so far, grouped by kind, each with a way
 * to take it out again. Long groups show their first few and open a modal
 * with the rest.
 */
@Component({
  selector: 'create-message-preview',
  standalone: true,
  imports: [],
  templateUrl: './message-preview.component.html',
  styleUrl: './message-preview.component.css',
})
export class MessagePreviewComponent {
  constructor() {}
  localCSVService = inject(LocalCsvService);
  private host: ElementRef<HTMLElement> = inject(ElementRef);

  take = take;
  formatCount = formatCount;

  /** How many of each group show before "See all". */
  readonly limit = 6;
  readonly fileLimit = 3;

  @Output() openPhoneNumberModal = new EventEmitter<void>();
  @Output() openContactsSelectedModal = new EventEmitter<void>();
  @Output() openCSVSelectedFilesResultModal = new EventEmitter<void>();
  @Output() openOrganizationsSelectedModal = new EventEmitter<void>();
  @Output() openTagsSelectedModal = new EventEmitter<void>();
  @Output() openSegmentsSelectedModal = new EventEmitter<void>();
  @Output() removePhoneNumber = new EventEmitter<string>();
  @Output() removeContact = new EventEmitter<IContact>();
  @Output() removeTag = new EventEmitter<ITag>();
  @Output() removeOrganization = new EventEmitter<IOrganization>();
  @Output() removeSegment = new EventEmitter<IEntitySegmentQuery>();

  // states and getters for preview data
  @Input() phoneNumbers: string[] = [];
  @Input() selectedContacts: IContact[] = [];
  @Input() selectedTags: ITag[] = [];
  @Input() selectedOrganizations: IOrganization[] = [];
  @Input() selectedSegments: IEntitySegmentQuery[] = [];

  get hasAny(): boolean {
    return (
      this.phoneNumbers.length > 0 ||
      this.selectedContacts.length > 0 ||
      this.selectedTags.length > 0 ||
      this.selectedOrganizations.length > 0 ||
      this.selectedSegments.length > 0 ||
      this.localCSVService.getAllFormattedFileData.length > 0
    );
  }

  contactName(contact: IContact): string {
    const name =
      (contact.fullName || '').trim() ||
      [contact.firstName, contact.lastName].filter(Boolean).join(' ').trim();
    return name || String(contact.primaryContactNumber || 'Unnamed contact');
  }

  /**
   * The pressed remove button goes with its chip. Move focus to the chip that
   * took its place (or the one before), then the group's "See all", then the
   * picker that adds that kind of recipient, so keyboard users stay put.
   */
  keepFocus(event: Event, group: 'numbers' | 'contacts' | 'tags' | 'organizations' | 'segments') {
    const pressed = event.currentTarget as HTMLElement | null;
    const list = pressed?.closest('ul');
    const index = list && pressed ? Array.from(list.querySelectorAll('.sel__remove')).indexOf(pressed) : 0;
    setTimeout(() => {
      const root = this.host.nativeElement;
      const section = root.querySelector<HTMLElement>(`[data-group="${group}"]`);
      const buttons = section ? Array.from(section.querySelectorAll<HTMLElement>('.sel__remove')) : [];
      const panel = root.closest('.compose__panel') ?? root.parentElement;
      const picker =
        group === 'numbers'
          ? panel?.querySelector<HTMLElement>('.rcp__tel input')
          : panel?.querySelector<HTMLElement>(`[data-source="${group}"]`);
      const target =
        buttons[Math.min(Math.max(index, 0), buttons.length - 1)] ??
        section?.querySelector<HTMLElement>('.bk-more') ??
        picker;
      target?.focus();
    });
  }

  removeFile(batchId: string) {
    this.localCSVService.removeFromAllFormattedById(batchId);
  }

  handleOpenPhoneNumberModal() {
    this.openPhoneNumberModal.emit();
  }

  handleOpenCSVSelectedFilesResultModal() {
    this.openCSVSelectedFilesResultModal.emit();
  }

  handleOpenContactsSelectedModal() {
    this.openContactsSelectedModal.emit();
  }

  handleOpenOrganizationsSelectedModal() {
    this.openOrganizationsSelectedModal.emit();
  }

  handleOpenTagsSelectedModal() {
    this.openTagsSelectedModal.emit();
  }

  handleOpenSegmentsSelectedModal() {
    this.openSegmentsSelectedModal.emit();
  }
}
